import React, { useState, useEffect } from 'react';
import { orderService } from '../api/orderService';
import { useNotifications } from '../context/NotificationContext';
import Table from '../components/Common/Table';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import OrderDetailsModal from '../components/Orders/OrderDetailsModal';
import { Eye, Download, Package, TrendingUp, Clock, CheckCircle, RefreshCw, Trash2 } from 'lucide-react';
import '../components/Common/Common.css';

function Orders() {
  const { markAsViewed, newItems } = useNotifications();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [flashingItems, setFlashingItems] = useState([]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;

      const response = await orderService.getOrders(params);
      setOrders(response.data);
      setLastPage(response.meta?.last_page || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const stats = await orderService.getOrderStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, searchTerm, statusFilter, sourceFilter]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  // Mark all orders as viewed when page loads
  useEffect(() => {
    markAsViewed('orders');
  }, []);

  // Flash new items
  useEffect(() => {
    if (newItems.orders && newItems.orders.length > 0) {
      const newIds = newItems.orders.map(item => item.id);
      setFlashingItems(newIds);

      // Remove flash after 3 seconds
      const timer = setTimeout(() => {
        setFlashingItems([]);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [newItems.orders]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      try {
        await orderService.deleteOrder(id);
        fetchOrders();
      } catch (error) {
        console.error('Error deleting order:', error);
        alert('Échec de la suppression de la commande');
      }
    }
  };


  const handleViewDetails = (order) => {
    setSelectedOrderId(order.id);
    setShowDetailsModal(true);
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      await orderService.downloadInvoice(orderId);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Échec du téléchargement de la facture');
    }
  };

  const handleStatusChange = () => {
    fetchOrders();
    fetchStatistics();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'badge-warning',
      accepted: 'badge-info',
      shipped: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-danger',
    };
    return <span className={`badge ${statusColors[status]}`}>{status}</span>;
  };

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    {
      key: 'invoice_number',
      label: 'N° Facture',
      render: (val) => val || 'N/A'
    },
    {
      key: 'client',
      label: 'Client',
      render: (client, row) => (
        <div>
          <div style={{ fontWeight: '500' }}>
            {client ? `${client.first_name} ${client.last_name}` : row.guest_name}
          </div>
          <div style={{ fontSize: '12px', color: '#718096' }}>
            {client ? client.email : row.guest_email}
          </div>
        </div>
      )
    },
    { key: 'total', label: 'Total', render: (val) => `${parseFloat(val).toFixed(2)} DH` },
    { key: 'status', label: 'Statut', render: getStatusBadge },
    {
      key: 'source',
      label: 'Source',
      render: (val) => (
        <span className={`badge ${val === 'website' ? 'badge-info' : 'badge-secondary'}`}>
          {val === 'website' ? 'Site Web' : 'Magasin'}
        </span>
      )
    },
    { key: 'created_at', label: 'Date', render: (val) => new Date(val).toLocaleDateString('fr-FR') },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Gestion des Commandes</h1>
        <button onClick={() => { fetchOrders(); fetchStatistics(); }} className="btn btn-primary">
          <RefreshCw size={18} />
          Actualiser
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '20px',
          marginBottom: '24px'
        }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
              <Package size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Total Commandes</div>
              <div className="stat-value">{statistics.total_orders || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
              <Clock size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">En attente</div>
              <div className="stat-value">
                {statistics.orders_by_status?.find(s => s.status === 'pending')?.count || 0}
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              <CheckCircle size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Terminées</div>
              <div className="stat-value">
                {statistics.orders_by_status?.find(s => s.status === 'completed')?.count || 0}
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
              <TrendingUp size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Revenu Total</div>
              <div className="stat-value">{parseFloat(statistics.total_revenue || 0).toFixed(2)} DH</div>
            </div>
          </div>
        </div>
      )}

      <p style={{ marginBottom: '20px', color: '#718096' }}>
        Gérez les commandes du site web et du magasin. Les commandes terminées apparaissent dans la section Ventes.
      </p>

      <div className="filters">
        <div className="filters-grid">
          <div style={{ gridColumn: 'span 2' }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher par nom, email, numéro de facture..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
          <div className="form-group">
            <label>Filtrer par statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Acceptée</option>
              <option value="shipped">Expédié</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <div className="form-group">
            <label>Filtrer par source</label>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="">Toutes les sources</option>
              <option value="website">Site Web</option>
              <option value="store">Magasin</option>
            </select>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={orders}
        loading={loading}
        actions={false}
        rowClassName={(row) => flashingItems.includes(row.id) ? 'flash-row' : ''}
        customActions={(row) => (
          <div className="action-buttons">
            <button
              onClick={() => handleViewDetails(row)}
              className="btn-icon"
              title="Voir les détails"
              style={{ color: '#667eea' }}
            >
              <Eye size={18} />
            </button>
            <button
              onClick={() => handleDownloadInvoice(row.id)}
              className="btn-icon"
              title="Télécharger la facture"
              style={{ color: '#10b981' }}
            >
              <Download size={18} />
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              className="btn-icon"
              title="Supprimer"
              style={{ color: '#ef4444' }}
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      {showDetailsModal && selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrderId(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

export default Orders;
